import { useEffect } from 'react';
import {
  type AnnotoriousOpenSeadragonAnnotator,
  type ImageAnnotation,
  useAnnotator,
  UserSelectAction,
} from '@annotorious/react';

import { getAnnotationProperties } from '@/lib/annotations';

/**
 * Two-way bridge between the workspace's selection set and Annotorious'
 * selection state. Selecting a box only makes its shape editable while it is
 * still a freshly drawn `temp` box; once a detection is committed to the
 * backend (`temp: false`) selecting it is highlight-only (`SELECT`), so its
 * bounding box can't be moved or resized.
 *
 * Clicks on committed boxes are handled through `clickAnnotation` rather than
 * `selectionChanged`: it carries the pointer event (so Shift can be read) and
 * fires on every click, whereas `selectionChanged` stays silent when the
 * clicked box is already Annotorious' current selection — which would make
 * Shift-toggling a box out of the set unreachable. `selectionChanged` covers
 * the two cases clicks on shapes don't: deselection (click on empty canvas)
 * and freshly drawn temp boxes, which the drawing tool selects itself.
 * Non-temp, non-empty selection events are echoes of shape clicks or of the
 * programmatic `setSelected` below, and are ignored; convergence holds
 * because the workspace reducer bails on no-op selection updates.
 */
interface UseSelectionBridgeProps {
  selectedIds?: ReadonlyArray<string>;
  /** Replace the selection with a single annotation, or clear it with `null`. */
  onSelectAnnotation?: (id: string | null) => void;
  /** Toggle an annotation in/out of the selection set (Shift + click). */
  onToggleAnnotation?: (id: string) => void;
}

export const useSelectionBridge = ({
  selectedIds,
  onSelectAnnotation,
  onToggleAnnotation,
}: UseSelectionBridgeProps) => {
  const seadragonAnnotator = useAnnotator<
    AnnotoriousOpenSeadragonAnnotator | undefined
  >();

  useEffect(() => {
    if (!seadragonAnnotator) return;
    // Only freshly drawn boxes stay editable; committed detections are
    // selectable (highlight-only) but their geometry is locked.
    seadragonAnnotator.setUserSelectAction((annotation: ImageAnnotation) =>
      getAnnotationProperties(annotation).temp
        ? UserSelectAction.EDIT
        : UserSelectAction.SELECT
    );

    const handleClick = (
      annotation: ImageAnnotation,
      originalEvent: PointerEvent
    ) => {
      // Temp boxes keep the selectionChanged flow so they become editable.
      if (getAnnotationProperties(annotation).temp) return;

      if (originalEvent.shiftKey) {
        onToggleAnnotation?.(annotation.id);
      } else {
        onSelectAnnotation?.(annotation.id);
      }
    };

    const handleSelectionChanged = (selected: Array<ImageAnnotation>) => {
      const first = selected[0];
      if (!first) {
        onSelectAnnotation?.(null);
      } else if (getAnnotationProperties(first).temp) {
        onSelectAnnotation?.(first.id);
      }
    };

    seadragonAnnotator.on('clickAnnotation', handleClick);
    seadragonAnnotator.on('selectionChanged', handleSelectionChanged);
    return () => {
      seadragonAnnotator.off('clickAnnotation', handleClick);
      seadragonAnnotator.off('selectionChanged', handleSelectionChanged);
    };
  }, [seadragonAnnotator, onSelectAnnotation, onToggleAnnotation]);

  useEffect(() => {
    if (!seadragonAnnotator) return;
    seadragonAnnotator.setSelected(
      selectedIds?.length ? [...selectedIds] : undefined
    );
  }, [seadragonAnnotator, selectedIds]);
};
